<template>
    <div class="kallelse-send">
        <div class="header">
            <h1>Skapa kallelse</h1>
            <k-button variant="hollow" @click="openKallelsemallarTab">
                <img src="@/assets/icons/gear_icon.svg" alt="Hantera kallelsemallar" />
                Hantera kallelsemallar
            </k-button>
        </div>
        <div class="container-select-kallelse">
            <KallelsemallAutocomplete label="Kallelsemall *" @update:modelValue="setSelectedKallelsemall" />
            <k-button
                variant="standard"
                size="secondary"
                type="button"
                @click="isPreviewDisplayed = true"
                :disabled="!selectedKallelsemall"
            >
                Förhandsvisa kallelse
            </k-button>
        </div>

        <div v-if="isPreviewDisplayed" class="preview-kallelse">
            <h3>Förhandsvisning kallelse</h3>
            <div>
                <template v-if="!isBokad">
                    <p v-if="isPrelbokad">
                        <img src="@/assets/info_dark.svg" alt="Varning" />
                        <span> Operationen är preliminärbokad </span>
                    </p>
                    <p v-else>
                        <img src="@/assets/warning_dark.svg" alt="Varning" />
                        <span> Operationen är ej bokad </span>
                    </p>
                </template>
                <AdvancedEditor
                    v-if="selectedKallelsemall?.body"
                    :content="selectedKallelsemall.body"
                    :fras-values="frasValues"
                    @update:content="updateContent"
                    type-label="Kallelsen"
                />
                <p v-else>Finns ingen text att visa</p>
            </div>
            <template>
                <ButtonGroup>
                    <k-button
                        type="button"
                        @click="createKallelse"
                        size="primary"
                        class="print"
                        :disabled="hasErrors || !canSkapaKallelse || !(isBokad || isPrelbokad)"
                    >
                        Skapa kallelse
                    </k-button>
                    <k-button type="button" size="secondary" variant="contour" @click="closeTab" class="cancel">
                        Avbryt
                    </k-button>
                </ButtonGroup>
            </template>
        </div>
        <ModalPdfPreview v-if="src" :src="src" title="Kallelse">
            <template #buttons>
                <k-button v-if="kivraConfigured" @click="sendKallelse">Skicka till Kivra</k-button>
                <k-button @click="printKallelse">Skriv ut kallelse</k-button>
            </template>
        </ModalPdfPreview>
    </div>
</template>

<script setup lang="ts">
    import { ref, onUnmounted, computed, onMounted, onBeforeMount, watch } from "vue";
    import isEqual from "lodash/isEqual";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import KallelsemallAutocomplete from "@/components/forms/reusables/KallelsemallAutocomplete.vue";
    import Kallelsemallar from "@/tabs/Operation/Kallelser/Kallelsemallar.vue";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import emitter from "tiny-emitter/instance";
    import AdvancedEditor from "@/components/Editor/AdvancedEditor.vue";
    import { useStore } from "@/store";
    import { flashMessage, flashWarning, openDialog } from "@/utils/index.js";
    import ModalPdfPreview from "@/components/generic/preview/ModalPdfPreview.vue";
    import { valideraFraser } from "@/components/Editor/helpers";
    import type { ValideraFraserCollection } from "@/components/Editor/helpers";
    import type { AxiosError } from "axios";
    import KButton from "@/components/Button.vue";

    import type { Kallelsemall, KallelsemallDisplay } from "@/store/modules/kallelsemallModule";
    import { convertKallelsemallData } from "@/store/modules/kallelsemallModule";

    type KallelseSendParams = {
        receiver?: {
            id?: string | number;
            patient?: string | number;
            patient_display?: {
                id?: string | number;
            };
        };
    };

    const store = useStore();

    const kallelsemallar = computed<Kallelsemall[]>(() => store.getters["kallelsemall/kallelsemallar"] ?? []);
    const isPreviewDisplayed = ref(false);
    const selectedKallelsemall = ref<KallelsemallDisplay | null>(null);
    const content = ref({});
    const defaultContent = ref({});
    const src = ref();
    const kallelseId = ref("");
    const anmalanId = ref<string | number | null>(null);
    const frasValues = ref<ValideraFraserCollection>(new Map());
    const errors = ref<Map<unknown, string[]> | null>(null);

    // Destructuring på props tar bort dess reaktivitet i Vue2. This is as intended
    // eslint-disable-next-line vue/no-setup-props-destructure
    const { tabId, parameters } = defineProps<{ tabId: number; parameters?: KallelseSendParams }>();

    // Quickfix för att lösa så att anmalan.value alltid är fylld, även om den inte finns i store.operations.
    const retrievedAnmalan = ref();
    const canSkapaKallelse = computed(() => store.getters["ability/can"]({ action: "kallelse", subject: "anmalan" }));
    const anmalan = computed(
        () =>
            store.getters["operation/operationsanmalan/getOpanmalanById"](parameters?.receiver?.id) ??
            retrievedAnmalan.value
    );

    const setSelectedKallelsemall = ({ value }: { value: unknown }) => {
        if (!value) {
            selectedKallelsemall.value = null;
            isPreviewDisplayed.value = false;
            return;
        }

        if (typeof value === "object") {
            selectedKallelsemall.value = value as KallelsemallDisplay;
            return;
        }

        const matchedKallelsemall =
            kallelsemallar.value.find((item: Kallelsemall) => item.id === (value as string | number)) ?? null;
        selectedKallelsemall.value = matchedKallelsemall
            ? (convertKallelsemallData({ kallelsemall: matchedKallelsemall }) as KallelsemallDisplay)
            : null;

        if (!selectedKallelsemall.value) {
            isPreviewDisplayed.value = false;
        }
    };

    const hasUnsavedData = computed(() => !isEqual(defaultContent.value, content.value));
    const hasErrors = computed(() => {
        return (errors?.value?.size ?? 0) > 0;
    });
    const isBokad = computed(() => anmalan?.value?.status === "Bokad");
    const isPrelbokad = computed(() => anmalan?.value?.preliminarbokning);
    const kivraConfigured = computed(() => store.getters["systemData/kivraConfigured"]);

    defineExpose({
        hasUnsavedData,
        hasErrors,
    });

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId,
            name: "Skapa kallelse",
        });
    };

    const callValideraFraser = async () => {
        const result = await valideraFraser({
            anmalanIds: parameters?.receiver?.id ? [String(parameters.receiver.id)] : [],
        });

        frasValues.value = result ?? new Map();
    };

    // TODO convert to useTabs in v3
    function closeTab({ override }: { override?: boolean }) {
        if (!override && hasUnsavedData.value) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId);
    }

    function openKallelsemallarTab() {
        store.dispatch("tabs/openTab", {
            component: Kallelsemallar,
        });
    }

    function resetValues() {
        selectedKallelsemall.value = null;
        isPreviewDisplayed.value = false;
    }

    function updateContent({
        data,
        errors: contentErrors,
    }: {
        data: Record<string, unknown> | string | null;
        errors: Map<unknown, string[]>;
    }) {
        content.value = data ?? {};
        errors.value = contentErrors;
    }

    async function printKallelse() {
        window.open(src.value, "_blank");

        await setUtskriven();
        emitter.emit("kallelse");
        flashMessage("Kallelse utskriven");

        closeTab({ override: true });
        resetValues();
    }

    async function sendKallelse() {
        if (!kivraConfigured.value) {
            openDialog("Kivra är inte konfigurerat", "error");

            return;
        }

        try {
            await klinikenApi.put(`/kallelser/${kallelseId.value}/kivra/`, {}, getConfig({}));
            await storeUpdatedOpAnmalan();

            emitter.emit("kallelse");
            flashMessage("Kallelse skickad till Kivra");
            closeTab({ override: true });
            resetValues();
        } catch (error) {
            const typedError = error as AxiosError;
            if (typedError.response?.status === 400) {
                openDialog(getErrorMessage(typedError), "information");
            } else {
                openDialog("Det gick inte att skicka kallelsen till Kivra", "error");
            }
        }
    }

    async function storeUpdatedOpAnmalan() {
        try {
            const response = await klinikenApi.get(`/operation/anmalan/${anmalanId.value}`, getConfig());

            if (response.status === 200) {
                store.commit("operation/operationsanmalan/setOpAnmala", response.data);
                store.commit("operation/operationsanmalanV2/updateOperationsanmalningar", {
                    subsetName: "newlyUpdated",
                    operationsanmalningar: [
                        {
                            ...response.data,
                            storeInfo: {
                                partial: false,
                            },
                        },
                    ],
                    offset: Object.keys(store.state.operation["operationsanmalanV2"].subsets.newlyUpdated.ids).length,
                    freezeAllSubsets: true,
                });
            }
        } catch (_) {
            //
        }
    }

    async function createKallelse() {
        if (errors.value?.size) {
            openDialog(Array.from(errors.value.values()).flat().join(","), "error");

            return;
        }

        if (!selectedKallelsemall.value) {
            openDialog("Välj en kallelsemall", "error");

            return;
        }

        // receiver.patient blir bara satt via /operation/koordingering men inte /operation/anmalan/{id}. Thus _display
        const patient = parameters?.receiver?.patient ?? parameters?.receiver?.patient_display?.id;
        const formData = {
            patient,
            body: content.value,
            mallnamn: selectedKallelsemall.value.namn,
            malltyp: selectedKallelsemall.value.typ,
            mallbody: selectedKallelsemall.value.body,
        };

        await store.dispatch("tabs/displaySpinner", { id: tabId, display: true });

        try {
            const { id } = parameters?.receiver ?? {};

            if (!id) {
                throw new Error("Ingen anmälan hittad");
            }

            const response = await klinikenApi.post(`/operation/anmalan/${id}/kallelse/`, formData, getConfig());

            if (response.status === 201) {
                anmalanId.value = id;
                kallelseId.value = response.data.id;

                emitter.emit("kallelse");
                flashMessage("Kallelse skapad");

                await storeUpdatedOpAnmalan();

                try {
                    const pdfResponse = await klinikenApi.get(
                        `/kallelser/${kallelseId.value}/pdf/`,
                        getConfig({ responseType: "blob" })
                    );

                    if (pdfResponse.status === 200) {
                        if (pdfResponse.data) {
                            src.value = URL.createObjectURL(pdfResponse.data);
                            return;
                        }
                    }

                    throw new Error("Fel vid skapande av PDF");
                } catch (error) {
                    openDialog(getErrorMessage(error), "error");
                }
                return;
            }

            throw new Error("Fel vid skapande av kallelse");
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        } finally {
            store.dispatch("tabs/displaySpinner", { id: tabId, display: false });
        }
    }

    async function setUtskriven() {
        try {
            await klinikenApi.put(`/kallelser/${kallelseId.value}/utskriven/`, {}, getConfig({}));
            await storeUpdatedOpAnmalan();
        } catch (error) {
            flashWarning(getErrorMessage(error));
        }
    }

    onMounted(async () => {
        // Quickfix för att lösa så att anmalan.value alltid är fylld, även om den inte finns i store.operations.
        if (!anmalan.value) {
            try {
                const response = await klinikenApi.get(`/operation/anmalan/${parameters?.receiver?.id}/`, getConfig());
                retrievedAnmalan.value = response.data;
            } catch (error) {
                openDialog(getErrorMessage(error), "error");
            }
        }
        watch(anmalan, async () => {
            await callValideraFraser();
        });
    });

    onBeforeMount(async () => {
        await callValideraFraser();
        setTabName();
    });

    // Om vald mall uppdateras, byt till senaste version for korrekt forhandsvisning.
    watch(kallelsemallar, (newOptions) => {
        if (!selectedKallelsemall.value?.value) {
            return;
        }

        const updated = (newOptions as Kallelsemall[]).find((item) => item.id === selectedKallelsemall.value?.value);

        if (updated) {
            selectedKallelsemall.value = convertKallelsemallData({ kallelsemall: updated }) as KallelsemallDisplay;
        }
    });

    onUnmounted(() => {
        resetValues();
    });
</script>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .kallelse-send {
        width: $content-width;
        max-width: 100%;

        .header {
            display: flex;
            align-items: center;
            gap: 40px;
            margin-bottom: 26px;

            h1 {
                margin: 0 !important;
            }
        }

        .container-select-kallelse {
            background-color: $color-blue-light;
            padding-top: 16px;
            padding-bottom: 25px;
            padding-left: 21px;
            padding-right: 21px;
            display: flex;
            column-gap: 40px;
            justify-content: center;
            align-items: center;

            button {
                flex-grow: 0;
                margin-top: 18px;
            }
        }

        .container-select-kallelse > * {
            flex-grow: 1;
        }

        .sms-receiver-container {
            margin-top: 10px;
            margin-bottom: 20px;

            div {
                margin-top: 10px;
                margin-bottom: 10px;

                display: flex;
                align-items: center;

                img {
                    margin-right: 8px;
                }
            }
        }

        .preview-kallelse {
            margin-top: 16px;

            div {
                margin: 16px 0px;

                p {
                    white-space: pre-line;
                }
            }

            .button-group {
                margin-top: 34px;
            }
        }
    }
</style>
